/**
 * Opening a story fast, before the tap on "Play" (docs/SKEL-STORY.md, "Opening a story fast").
 *
 * An open used to be a chain: the story row, then `bundle.json`, then the renderer's code, then
 * the textures. `prefetchBundle` starts `bundle.json` when an open is likely (a pointer resting on
 * "Play", the story's own page) and warms what the first scene draws with — the pack pages and the
 * plate, content-addressed and immutable, so the player's own requests are then answered by the
 * browser cache. `warmPlayerCode` downloads the renderer's lazily loaded chunks beside the bundle
 * instead of after it. `standalone.ts#fetchBundle` takes a prefetched copy when there is a fresh
 * one.
 *
 * No renderer, no DOM beyond `fetch`: safe to import from the SPA's list pages.
 */

export class HttpError extends Error {
  readonly status: number
  constructor(status: number, url: string) {
    super(`HTTP ${status} for ${url}`)
    this.name = 'HttpError'
    this.status = status
  }
}

/** `bundle.json` from the network (the browser revalidates it: `no-cache` + ETag → 304). */
export async function fetchBundleJson(href: string, signal?: AbortSignal): Promise<unknown> {
  const res = await fetch(href, { credentials: 'same-origin', ...(signal ? { signal } : {}) })
  if (!res.ok) throw new HttpError(res.status, href)
  return (await res.json()) as unknown
}

/** How long a prefetched bundle stays good for the open that follows it. */
export const PREFETCH_FRESH_MS = 30_000
/**
 * How many prefetched bundles are held at once. Each is a parsed 0.2-0.8 MB `bundle.json` (a few
 * MB of heap), and a pointer resting on one card after another would otherwise keep every story
 * it passed for the rest of the session.
 */
export const PREFETCH_KEEP = 3

const prefetched = new Map<string, { at: number; body: Promise<unknown> }>()
const warmed = new Set<string>()

/** Drop what can no longer be handed out: stale entries, then the oldest past `PREFETCH_KEEP`. */
function sweep(now: number): void {
  for (const [key, hit] of prefetched) if (now - hit.at > PREFETCH_FRESH_MS) prefetched.delete(key)
  for (const key of [...prefetched.keys()].slice(0, Math.max(0, prefetched.size - PREFETCH_KEEP))) prefetched.delete(key)
}

/** Test seam: how many prefetched bundles are held. */
export function prefetchedCount(): number {
  return prefetched.size
}

function absolute(url: URL | string): string {
  if (typeof url !== 'string') return url.href
  try {
    return new URL(url, typeof location !== 'undefined' ? location.href : 'http://localhost/').href
  } catch {
    return url
  }
}

/**
 * The prefetched copy of `href`, if one is fresh; it is handed out once (a later open asks the
 * network again, so a re-baked story is never served from a stale copy).
 */
export function takePrefetched(href: string): Promise<unknown> | null {
  const key = absolute(href)
  const hit = prefetched.get(key)
  if (!hit) return null
  prefetched.delete(key)
  return Date.now() - hit.at <= PREFETCH_FRESH_MS ? hit.body : null
}

/**
 * Evict a prefetched bundle (or all) so subsequent opens fetch the latest bundle from the server.
 */
export function evictPrefetched(url?: URL | string): void {
  if (!url) {
    prefetched.clear()
    return
  }
  prefetched.delete(absolute(url))
}

export interface PrefetchOptions {
  /**
   * Also warm the first scene's pack pages, plate and voice once the bundle is in (default true).
   * A finger coming down on a card asks for `false`: most touches on a list are the start of a
   * scroll, and ~0.5 MB of pictures and voice per card a thumb passes over is a phone's data.
   */
  warm?: boolean
}

/**
 * Start fetching a story's `bundle.json` now and, once it is in, warm its first scene's pages and
 * plate. Idempotent while fresh. The promise rejects like `fetchBundleJson`; callers that only
 * prefetch should ignore that.
 */
export function prefetchBundle(url: URL | string, opts: PrefetchOptions = {}): Promise<unknown> {
  const href = absolute(url)
  const now = Date.now()
  sweep(now)
  let hit = prefetched.get(href)
  if (!hit) {
    const body = fetchBundleJson(href)
    hit = { at: now, body }
    prefetched.set(href, hit)
    const forget = (): void => {
      if (prefetched.get(href)?.body === body) prefetched.delete(href)
    }
    body.catch(forget)
    // Unused, it goes once it is stale (`takePrefetched` would refuse it anyway), not at the next sweep.
    const timer = setTimeout(forget, PREFETCH_FRESH_MS + 1_000) as unknown as { unref?: () => void }
    timer.unref?.()
    sweep(now) // the newest is last in the map, so the cap drops the oldest
  }
  if (opts.warm !== false) {
    hit.body.then(
      (bundle) => warmFirstScene(bundle, new URL('.', href)),
      () => undefined,
    )
  }
  return hit.body
}

interface BundleShape {
  assets?: Record<string, { kind?: unknown; url?: unknown } | undefined>
  cast?: Record<string, { sprite?: { clips?: Record<string, { frames?: { asset?: unknown }[] }> } }> | { sprite?: { clips?: Record<string, { frames?: { asset?: unknown }[] }> } }[]
  scenes?: { plate?: { asset?: unknown; poster?: unknown }; cues?: { op?: unknown; audio?: { url?: unknown } | null }[] }[]
}

const SPOKEN = new Set(['narrate', 'say', 'question'])

/**
 * What stands between an open and "Tap to start": the pack pages, scene 0's plate, and scene 0's
 * baked voice (the player waits on those lines, briefly, so the child hears Kokoro from the first
 * word).
 */
export function firstSceneUrls(bundle: unknown, baseUrl: URL | string): string[] {
  const b = bundle as BundleShape | null
  const assets = b?.assets
  if (!assets || typeof assets !== 'object') return []
  const scene = b?.scenes?.[0]
  const plate = scene?.plate
  const raw: unknown[] = []
  for (const id of [plate?.asset, plate?.poster]) if (typeof id === 'string') raw.push(assets[id]?.url)
  for (const a of Object.values(assets)) if (a?.kind === 'pack') raw.push(a.url)
  for (const entry of Object.values(b?.cast ?? {})) {
    for (const clip of Object.values(entry?.sprite?.clips ?? {})) {
      for (const frame of clip.frames ?? []) if (typeof frame.asset === 'string') raw.push(assets[frame.asset]?.url)
    }
  }
  for (const cue of scene?.cues ?? []) if (SPOKEN.has(String(cue?.op))) raw.push(cue?.audio?.url)
  const out = new Set<string>()
  for (const url of raw) {
    if (typeof url !== 'string' || url.length === 0) continue
    try {
      out.add(new URL(url, baseUrl).href)
    } catch {
      /* the loader will say so */
    }
  }
  return [...out]
}

function warmFirstScene(bundle: unknown, baseUrl: URL | string): void {
  if (typeof fetch === 'undefined') return
  for (const url of firstSceneUrls(bundle, baseUrl)) {
    if (warmed.has(url)) continue
    warmed.add(url)
    // Low priority, and read to the end so the HTTP cache keeps it. A failure only means the
    // player fetches it itself.
    void fetch(url, { credentials: 'same-origin', priority: 'low' } as RequestInit)
      .then((res) => (res.ok ? res.arrayBuffer() : undefined))
      .catch(() => warmed.delete(url))
  }
}

/**
 * Start downloading the player's lazily loaded code (the renderer, the speech bubbles and pixi)
 * now: `createPlayer` imports the same modules, so its own imports then resolve from the module
 * cache. Skipped where there is no WebGL (nothing would draw with it; also jsdom). Never throws.
 */
export function warmPlayerCode(): void {
  if (typeof window === 'undefined' || typeof (window as { WebGLRenderingContext?: unknown }).WebGLRenderingContext === 'undefined') return
  void Promise.all([import('./stage'), import('./world'), import('./bubbles'), import('pixi.js')]).catch(() => undefined)
}

/** Test seam: forget every prefetched bundle and warmed url. */
export function resetPrefetch(): void {
  prefetched.clear()
  warmed.clear()
}
